<script>
    import { sendAnalytics } from "@/core/SendAnalytics";
    import { apiUrl } from "@/utils";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductColorsList from "@/components/ProductColorsList";
    import ProductCardPrice from "@ledger/components/ProductCardPrice.vue";
    import ProductCardIcons from "@ledger/components/ProductCardIcons";
    import ProductCardOptions from "@ledger/components/ProductCardOptions";
    import ProductDiscount from "@ledger/components/ProductDiscount.vue";

    export default {
        name: "ProductCard",
        components: {
            ProductDiscount,
            ProductCardOptions,
            ProductCardIcons,
            ProductCardButtons,
            ProductCardPrice,
            ProductColorsList,
        },
        props: {
            product: Object,
            position: Number,
            inCartList: Array,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                offerColorName: currentOffers[0].color?.name,
                previewImage,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                loading: false,
                isPreOrder,
                previewImageFromOffer: "",
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            imageSrc() {
                return this.previewImageFromOffer ? apiUrl(this.previewImageFromOffer) : "";
            },
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug, offer: this.offerId },
                };
            },
            productInfo() {
                let filtered = {
                    "ledger-nano-x": {
                        disabledIcon: "",
                        disabledOptions: [],
                        bluetooth: true,
                    },
                    "ledger-nano-s": {
                        disabledIcon: "ios",
                        disabledOptions: ["Bluetooth", "Аккумулятор"],
                        bluetooth: false,
                    },
                };
                return filtered[this.product.slug];
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
            selectColor(color) {
                const offer = this.currentOffers.find((offer) => offer.color.id === color.id);
                if (offer) {
                    const { id, previewImage, price, discountPrice, available } = offer;
                    this.offerId = id;
                    this.offerColorName = color.name;
                    this.previewImageFromOffer = previewImage.medium;
                    this.price = price;
                    this.discountPrice = discountPrice;
                    this.isPreOrder = !available;
                    this.inCart = false;
                }
            },
        },
    };
</script>

<template>
    <div class="product-card">
        <img
            v-if="productInfo.bluetooth"
            class="product-card__label"
            src="/static/ledger/icons/products/bluetooth.svg"
            alt=""
        />

        <router-link
            :to="productPath"
            class="product-card__image js-lazy-load"
            @click.native.prevent="sendDataLayerEvent"
        >
            <img :src="imageSrc" :data-src="apiUrl(previewImage)" />

            <div class="product-card__preloader">
                <preloader :show="true" lg-size is-color></preloader>
            </div>
        </router-link>

        <div class="product-card__info">
            <div class="product-card__top">
                <router-link
                    :to="productPath"
                    class="product-card__title"
                    @click.native.prevent="sendDataLayerEvent"
                >
                    <span>{{ product.shortName }}</span>
                    <span v-if="discountPercent" class="product-card__discount">
                        <product-discount :percent="discountPercent"></product-discount>
                    </span>
                </router-link>

                <div
                    v-if="offer.color && currentOffers.length > 1"
                    class="product-card__colors"
                >
                    <product-colors-list
                        :offers="currentOffers"
                        :current-offer="offer"
                        @select-color="selectColor"
                    ></product-colors-list>

                    <span v-if="offerColorName" class="product-card__color-name">
                        {{ offerColorName }}
                    </span>
                </div>

                <div class="product-card__icons">
                    <product-card-icons
                        :disabled="productInfo.disabledIcon"
                    ></product-card-icons>
                </div>
            </div>
            <div class="product-card__bottom">
                <product-card-options
                    :disabled="productInfo.disabledOptions"
                ></product-card-options>
            </div>
        </div>

        <div class="product-card__actions">
            <router-link
                :to="productPath"
                class="product-card__price"
                @click.native.prevent="sendDataLayerEvent"
            >
                <product-card-price
                    :price="discountPrice"
                    :old-price="price"
                    :discount-count="discountPercent"
                ></product-card-price>
            </router-link>

            <product-card-buttons
                :offer-id="offerId"
                :product="product"
                :is-pre-order="isPreOrder"
                :in-cart="inCart"
                :discount-price="discountPrice"
                size-button="lg"
                is-tech-event
                @on-added-to-cart="inCart = true"
            ></product-card-buttons>
        </div>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: block;
        background-color: var(--primary-background);
        border: 1px solid transparent;
        transition: border-color 0.2s ease-in;
        &:hover {
            border-color: rgba(0, 0, 0, 0.5);
        }
        @include bp($bp-desktop-sm) {
            display: flex;
        }
    }

    .product-card__image {
        position: relative;
        display: block;
        min-height: 240px;
        &::before {
            content: "";
            position: relative;
            z-index: 1;
            display: block;
            padding-bottom: 75%;
            border-bottom: 1px solid rgba(0, 0, 0, 0.5);
        }
        img {
            @include center-absolute(0);
            display: none;
            height: 100%;
            margin: 0 auto;
            animation: fadeIn 0.3s ease-in-out;

            @keyframes fadeIn {
                from {
                    opacity: 0;
                    transform: scale(0.9);
                }
                to {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            &[lazy="loaded"] {
                display: block;
                & ~ .product-card__preloader {
                    opacity: 0;
                }
            }
        }
        @include bp($bp-desktop-sm) {
            flex-shrink: 0;
            width: 400px;
            height: 400px;
            margin: 0;
            border-right: 1px solid rgba(0, 0, 0, 0.5);
            &::before {
                padding-bottom: 100%;
                border-bottom: none;
            }
        }
    }

    .product-card__info {
        display: flex;
        flex-direction: column;
        @include bp($bp-desktop-sm) {
            flex-grow: 1;
        }
    }

    .product-card__top {
        flex-grow: 1;
        padding: 20px 20px 30px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.5);
        @include bp($bp-desktop-sm) {
            padding: 40px 37px 0;
        }
    }

    .product-card__bottom {
        padding: 20px;
        @include bp($bp-desktop-sm) {
            padding: 17px 37px 37px;
        }
    }

    .product-card__actions {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        padding: 30px 20px 20px;
        border-top: 1px solid rgba(0, 0, 0, 0.5);
        @include bp($bp-desktop-sm) {
            flex-basis: 277px;
            max-width: 277px;
            flex-shrink: 0;
            padding-top: 40px;
            border-top: none;
            border-left: 1px solid rgba(0, 0, 0, 0.5);
        }
    }

    .product-card__price {
        margin-bottom: 40px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 0;
        }
    }

    .product-card__title {
        display: flex;
        align-items: center;
        margin-bottom: 20px;
        color: var(--primary-text);
        font-family: var(--secondary-font);
        font-size: 24px;
        font-weight: 700;
        line-height: 28px;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        @include bp($bp-desktop-sm) {
            font-size: 40px;
            line-height: 40px;
        }
    }

    .product-card__colors {
        margin-bottom: 30px;
        @include bp($bp-desktop-sm) {
            display: flex;
            align-items: center;
            margin-bottom: 35px;
        }
    }

    .product-card__color-name {
        display: none;
        @include bp($bp-desktop-sm) {
            display: block;
            margin-left: 20px;
            font-size: 8px;
            line-height: 8px;
        }
    }

    .product-card__discount {
        margin-left: 10px;
        @include bp($bp-desktop-sm) {
            margin-left: 20px;
        }
    }

    .product-card__icons {
        @include bp($bp-desktop-sm) {
            max-width: 410px;
        }
    }

    .product-card__label {
        position: absolute;
        z-index: 2;
        top: 20px;
        left: 20px;
        width: 32px;
        height: 42px;
        pointer-events: none;
        @include bp($bp-desktop-sm) {
            width: 56px;
            height: 74px;
        }
    }
</style>
